import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import iconFlechaAbajo from "../assets/icons/angle-down.svg";
// import iconImage from "../assets/icons/image.svg"; // Unused import removed
import { useAuth } from "../auth/AuthContext";

export default function Chat({ onClose, chatData: propChatData, isFullPage = false }) {
  const { user } = useAuth(); // authFetch not currently used in fetch calls below, but kept context

  const context = useOutletContext() || {};
  const chatData = propChatData || context.chatData;

  // State
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Ref for auto-scrolling (optional but recommended)
  const messagesEndRef = useRef(null);

  // Helper: Get formatted time
  const getFormattedTime = (dateString = null) => {
    const date = dateString ? new Date(dateString) : new Date();
    return (
      date.toLocaleDateString("en-US") +
      ", " +
      date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    );
  };

  const defaultWelcomeMessage = {
    id: "welcome-msg",
    sender: "bot",
    text: "Hello, how can I help you?",
    time: null
  };

  // EFFECT: Sync chatData with local state
  // This ensures that when the parent component fetches data, the chat updates
  useEffect(() => {


    if (chatData && chatData.messages && chatData.messages.length > 0) {

      setChatId(chatData.id);

      const formattedMessages = [...chatData.messages]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .map((msg) => ({
          id: msg.id,
          sender: msg.sender_id === user?.id ? "user" : "bot",
          text: msg.content,
          time: getFormattedTime(msg.created_at),
        }));

      setMessages([defaultWelcomeMessage, ...formattedMessages]);
    } else {
      // New chat or empty data

      setChatId(null);
      setMessages([defaultWelcomeMessage]);
    }
  }, [chatData, user?.id]);

  // EFFECT: Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    // 1. Optimistic UI Update
    const userMessage = {
      id: Date.now(),
      sender: "user",
      text,
      time: getFormattedTime(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue("");
    setIsLoading(true);

    // Prepare payload for AI
    const payload = updatedMessages.map((msg) => ({
      role: msg.sender === "user" ? "user" : "assistant",
      content: msg.text,
    }));

    let activeChatId = chatId;

    try {
      // 2. If this is a new chat (no ID yet), create it in the DB
      if (!activeChatId) {


        const insertChatPayload = {
          owner_id: user?.id,
          last_message: text,
        };

        const chatsEndpoint = `${process.env.REACT_APP_API_URL}/api/chat/chats`;

        const response = await fetch(chatsEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(insertChatPayload),
        });

        const data = await response.json().catch(() => null);

        if (data && data.chatId) {
          activeChatId = data.chatId;
          setChatId(data.chatId);

        }
      }

      // 3. Send message to AI (SaberesGPT)
      const aiResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/saberes-gpt`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: payload }),
        }
      );

      if (!aiResponse.ok) throw new Error("Server response error");

      const aiData = await aiResponse.json();
      const botResponseText = aiData.output || "Response received.";

      const botMessage = {
        id: Date.now() + 1,
        sender: "bot",
        text: botResponseText,
        time: getFormattedTime(),
      };

      // Update UI with Bot Response
      setMessages((prev) => [...prev, botMessage]);

      // 4. Save messages to Database (Background operation)
      // Only proceed if we have a valid chat ID
      if (activeChatId) {
        const messagesEndpoint = `${process.env.REACT_APP_API_URL}/api/chat/messages`;
        const saberesGPTuserId = process.env.REACT_APP_SABERESGPT_USERID;

        const userPayload = {
          chat_id: activeChatId,
          sender_id: user?.id,
          content: text,
        };

        const botPayload = {
          chat_id: activeChatId,
          sender_id: saberesGPTuserId,
          content: botResponseText,
        };

        // Send user message first, then bot message serially
        await fetch(messagesEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(userPayload),
        }).catch((e) => console.error("Error saving user msg:", e));

        await fetch(messagesEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(botPayload),
        }).catch((e) => console.error("Error saving bot msg:", e));
      }

    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage = {
        id: Date.now() + 2,
        sender: "bot",
        text: "Sorry, there was an error connecting to the server.",
        time: getFormattedTime(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRefresh = async () => {
    if (chatId) {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/chat/chats/${chatId}/archive`, {
          method: "PATCH"
        });
      } catch (err) {
        console.error("Error archiving chat:", err);
      }
    }
    setChatId(null);
    setMessages([defaultWelcomeMessage]);
    setInputValue("");
    setIsLoading(false);
  };

  return (
    <div className={isFullPage
      ? "mt-4 w-full h-[calc(100dvh-230px)] sm:h-[calc(100dvh-150px)] flex flex-col"
      : "fixed bottom-4 right-4 sm:bottom-24 sm:right-6 w-[calc(100dvw-32px)] sm:w-[432px] h-[calc(100dvh-120px)] sm:h-[600px] max-h-[85dvh] z-[9998]"}>
      <div className="w-full h-full rounded-2xl border border-white/45 bg-white/35 backdrop-blur-md shadow-[0_18px_45px_rgba(15,23,42,0.18)] flex flex-col overflow-hidden relative">
        {!isFullPage && onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/85 backdrop-blur-sm hover:bg-white shadow-sm border border-white/70 transition-all"
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {/* Zona de mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-3 bg-white/55 relative">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`mb-3 flex ${msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl text-[15px] leading-[1.5] font-normal ${msg.sender === "user"
                  ? "bg-[#1E5B4F] text-white border border-[#C49A1F]/60 shadow-sm"
                  : "bg-slate-100 text-slate-900 border border-slate-300 shadow-sm"
                  }`}
              >
                {msg.sender === "bot" ? (
                  <div className="prose prose-sm max-w-none text-current prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-li:my-0 prose-a:text-blue-600 prose-strong:text-current">
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{msg.text}</div>
                )}
                <div className="mt-1 text-[11px] opacity-70 text-right">
                  {msg.time}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="mb-3 flex justify-start">
              <div className="bg-slate-100 border border-slate-300 px-4 py-3 rounded-xl text-[15px] text-slate-600 italic shadow-sm">
                Thinking...
              </div>
            </div>
          )}
          {/* Invisible element to auto-scroll to */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input inferior */}
        <div className="h-16 px-4 py-2 bg-white/95 border-t border-slate-200/90 backdrop-blur-sm flex items-center gap-2 shadow-[0_-4px_14px_rgba(15,23,42,0.06)]">
          {/* Botón de nueva conversación */}
          <button
            type="button"
            onClick={handleRefresh}
            className="w-8 h-8 flex items-center justify-center hover:bg-white rounded-full transition-colors border border-transparent hover:border-white/70"
            title="New conversation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 text-gray-500"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
          </button>

          <div className="flex-1">
            <div className="w-full bg-white border border-slate-300 rounded-full px-4 py-2 flex items-center shadow-sm focus-within:border-[#1E5B4F]/70 focus-within:ring-2 focus-within:ring-[#C49A1F]/35 transition-all">
              <textarea
                className="w-full bg-transparent text-[14px] text-slate-900 placeholder:text-slate-400 outline-none resize-none leading-tight"
                rows={1}
                placeholder="Aa"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Botón de enviar mensaje */}
          <button
            type="button"
            className={`h-9 w-9 rounded-full grid place-items-center border border-[#C49A1F]/70 bg-[#1E5B4F]/90 transition-colors ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1E5B4F]"}`}
            onClick={handleSend}
            disabled={isLoading}
            aria-label="Send message"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 text-white"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}